import React, { useState, useEffect } from 'react';
import { MessageCircle, CheckCircle, XCircle, Trash2, Eye, Filter } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const CommentManager = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedComment, setSelectedComment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchComments();
  }, [statusFilter]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = statusFilter === 'all' 
        ? `${API_BASE}/api/admin/blog/comments`
        : `${API_BASE}/api/admin/blog/comments?status=${statusFilter}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      } else {
        throw new Error('Failed to fetch comments');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load comments',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (comment, type) => {
    setSelectedComment(comment);
    setActionType(type);
    setAdminNotes('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedComment(null);
    setActionType(null);
    setAdminNotes('');
  };

  const handleAction = async () => {
    if (!selectedComment || !actionType) return;

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/blog/comments/${selectedComment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: actionType === 'approve' ? 'approved' : 'rejected',
          admin_notes: adminNotes || null
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Comment ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`,
        });
        closeModal();
        fetchComments();
      } else {
        throw new Error('Failed to update comment');
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to update comment status',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/blog/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: 'Comment deleted successfully',
        });
        fetchComments();
      } else {
        throw new Error('Failed to delete comment');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete comment',
        variant: 'destructive'
      });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Pending</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Rejected</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            Blog Comments
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            Manage and moderate blog post comments
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('pending')}
          className={statusFilter === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
        >
          <Filter className="w-4 h-4 mr-2" />
          Pending
        </Button>
        <Button
          variant={statusFilter === 'approved' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('approved')}
          className={statusFilter === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Approved
        </Button>
        <Button
          variant={statusFilter === 'rejected' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('rejected')}
          className={statusFilter === 'rejected' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          <XCircle className="w-4 h-4 mr-2" />
          Rejected
        </Button>
        <Button
          variant={statusFilter === 'all' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('all')}
        >
          All
        </Button>
      </div>

      {/* Comments List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <MessageCircle className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 dark:text-gray-400 mb-3" />
            <p className="text-gray-600 dark:text-gray-400">
              No {statusFilter !== 'all' ? statusFilter : ''} comments found
            </p>
          </div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className="p-3 sm:p-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
            >
              {/* Header */}
              <div className="flex flex-col sm:flex-row items-start justify-between mb-4 gap-3">
                <div className="flex-1 w-full">
                  <div className="flex items-start gap-2 sm:gap-3 mb-2">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold text-sm sm:text-base">
                      {comment.author_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100 truncate">
                        {comment.author_name}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                        {comment.author_email}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {getStatusBadge(comment.status)}
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1 line-clamp-2">
                    Post: <span className="font-medium">{comment.post_title}</span>
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 dark:text-gray-400">
                    {formatDate(comment.created_at)}
                  </p>
                </div>
              </div>

              {/* Comment Content */}
              <div className="mb-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                  {comment.content}
                </p>
              </div>

              {/* Admin Notes */}
              {comment.admin_notes && (
                <div className="mb-4 p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border dark:border-gray-700-blue-800 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                    Admin Notes:
                  </p>
                  <p className="text-xs sm:text-sm text-blue-800 dark:text-blue-400 break-words">
                    {comment.admin_notes}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2">
                {comment.status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                      onClick={() => openActionModal(comment, 'approve')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      <span className="text-xs sm:text-sm">Approve</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                      onClick={() => openActionModal(comment, 'reject')}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      <span className="text-xs sm:text-sm">Reject</span>
                    </Button>
                  </>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none"
                  onClick={() => handleDelete(comment.id)}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span className="text-xs sm:text-sm">Delete</span>
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Action Modal */}
      {showModal && selectedComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-4 border-b dark:border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold dark:text-gray-100 flex items-center gap-2">
                {actionType === 'approve' ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Approve Comment
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-600" />
                    Reject Comment
                  </>
                )}
              </h3>
            </div>

            <div className="p-3 sm:p-4">
              <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 break-words">
                  {selectedComment.content}
                </p>
              </div>

              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Admin Notes (Optional)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add any notes about this decision..."
                rows={3}
                className="w-full px-2 sm:px-3 py-2 border border-gray-300 dark:border dark:border-gray-700-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-gray-100 text-xs sm:text-sm resize-none"
                disabled={submitting}
              />
            </div>

            <div className="p-3 sm:p-4 border-t dark:border-gray-700 flex flex-col-reverse sm:flex-row gap-2 justify-end">
              <Button
                variant="outline"
                onClick={closeModal}
                disabled={submitting}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                variant={actionType === 'approve' ? 'default' : 'destructive'}
                onClick={handleAction}
                disabled={submitting}
                className={`w-full sm:w-auto ${actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}`}
              >
                {submitting ? 'Processing...' : (actionType === 'approve' ? 'Approve' : 'Reject')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentManager;
